const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// Deduplicate concurrent refresh-token calls (mirrors baseApi.js behaviour).
let refreshPromise = null;

async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) return null;
        const json = await res.json().catch(() => null);
        const token = json?.data?.token || json?.token;
        if (token) {
          localStorage.setItem('token', token);
          return token;
        }
        return null;
      })
      .catch(() => null)
      .finally(() => {
        setTimeout(() => { refreshPromise = null; }, 0);
      });
  }
  return refreshPromise;
}

async function fetchSignature(token, folder) {
  return fetch(`${API_BASE_URL}/admin/cloudinary/signature`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ folder, resourceType: 'auto' }),
  });
}

export async function uploadToCloudinary(file, folder, onProgress = null) {
  let token = localStorage.getItem('token');
  if (!token) throw new Error('Authentication token not found');

  // Get upload signature (with refresh-on-401 retry)
  let signatureResponse = await fetchSignature(token, folder);

  if (signatureResponse.status === 401 || signatureResponse.status === 403) {
    const newToken = await refreshAccessToken();
    if (!newToken) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:session-expired'));
      }
      throw new Error('Session expired. Please log in again.');
    }
    token = newToken;
    signatureResponse = await fetchSignature(token, folder);
  }

  if (!signatureResponse.ok) {
    const error = await signatureResponse.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to get upload signature');
  }

  const { data: signatureData } = await signatureResponse.json();
  const { signature, timestamp, apiKey, uploadUrl } = signatureData;
  
  // Upload to Cloudinary
  const formData = new FormData();
  formData.append('file', file);
  formData.append('api_key', apiKey);
  formData.append('timestamp', timestamp);
  formData.append('signature', signature);
  formData.append('folder', folder);
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
    }
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText));
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });
    
    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
    
    xhr.open('POST', uploadUrl);
    xhr.send(formData);
  });
}

export async function uploadMediaFiles(files, baseFolder) {
  const uploads = [];
  
  // Support for legacy file types
  const legacyFileTypes = {
    image: 'images',
    audio: 'audio',
    extraImage: 'images'
  };
  
  // Handle legacy file types first
  Object.entries(legacyFileTypes).forEach(([fileKey, folder]) => {
    if (files[fileKey]) {
      uploads.push(
        uploadToCloudinary(files[fileKey], `${baseFolder}/${process.env.NODE_ENV === 'development' ? 'dev/' : ''}${folder}`)
          .then(result => ({ type: fileKey, url: result.secure_url }))
      );
    }
  });
  
  // Handle dynamic file types (for homepage and other custom uploads)
  Object.keys(files).forEach(fileKey => {
    if (!legacyFileTypes[fileKey] && files[fileKey]) {
      // All custom file types are treated as images
      uploads.push(
        uploadToCloudinary(files[fileKey], `${baseFolder}/${process.env.NODE_ENV === 'development' ? 'dev/' : ''}images`)
          .then(result => ({ type: fileKey, url: result.secure_url }))
      );
    }
  });
  
  const results = await Promise.all(uploads);
  return results.reduce((acc, { type, url }) => {
    acc[`${type}Url`] = url;
    return acc;
  }, {});
}
